import { useEffect, useRef } from 'react'
import { Alert, App, Form, Modal, Space } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { createReceipt } from '@/features/cashdesk/cashdeskApi'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import { useReceiptPrinter } from '@/features/cashdesk/useReceiptPrinter'
import type { InvoiceDetail } from './invoiceApi'

interface PaymentForm {
  branchId?: string
  amount: Money | null
}

interface InvoicePaymentModalProps {
  invoice: InvoiceDetail | null
  onClose: () => void
}

export function InvoicePaymentModal({ invoice, onClose }: InvoicePaymentModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const branches = useCashBranches()
  const printer = useReceiptPrinter()
  const { message } = App.useApp()
  const [form] = Form.useForm<PaymentForm>()
  const key = useRef('')
  const needsBranchChoice = !branches.unknown && branches.active.length > 1

  useEffect(() => {
    if (invoice === null) return
    key.current = newIdempotencyKey('cash-receipt')
    form.setFieldsValue({ branchId: invoice.branch.id, amount: invoice.outstanding })
  }, [invoice, form])

  useEffect(() => {
    if (printer.error) void message.error(translateError(printer.error, t))
  }, [printer.error, message, t])

  const pay = useMutation({
    mutationFn: (values: PaymentForm) =>
      createReceipt(
        {
          subscriberId: invoice?.customer.subscriberId ?? '',
          branchId: branches.sole?.id ?? values.branchId ?? null,
          purpose: 'TOPUP',
          amount: values.amount?.amount ?? 0,
          planId: null,
          serviceName: null,
          invoiceId: invoice?.id ?? null,
        },
        key.current,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
      void printer.print(result.receipt)
      onClose()
    },
  })

  return (
    <>
      <Modal
        open={invoice !== null}
        title={t('invoices.payment.title', { no: invoice?.invoiceNo ?? '' })}
        okText={t('cashdesk.payment.submit')}
        cancelText={t('common.cancel')}
        confirmLoading={pay.isPending}
        onCancel={() => {
          pay.reset()
          onClose()
        }}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => pay.mutate(values))}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {invoice ? (
            <Alert
              type="info"
              showIcon
              message={t('invoices.payment.outstanding', { amount: formatMoneyValue(invoice.outstanding) })}
            />
          ) : null}
          {pay.isError ? <Alert type="error" showIcon message={translateError(pay.error, t)} /> : null}
          <Form<PaymentForm> form={form} layout="vertical" requiredMark preserve={false}>
            {needsBranchChoice ? (
              <Form.Item
                name="branchId"
                label={t('cashdesk.branch')}
                rules={[{ required: true, message: t('cashdesk.branchRequired') }]}
              >
                <CashBranchSelect branches={branches.active} loading={branches.isPending} style={{ width: '100%' }} />
              </Form.Item>
            ) : null}
            <Form.Item
              name="amount"
              label={t('cashdesk.payment.amount')}
              extra={t('invoices.payment.hint')}
              rules={[
                {
                  validator: (_, value: Money | null | undefined) =>
                    value && value.amount > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('cashdesk.payment.amountRequired'))),
                },
              ]}
            >
              <MoneyInput currency={invoice?.total.currency ?? ''} minMinor={1} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
      {printer.portal}
    </>
  )
}
